import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value: number | null | undefined, compact = false): string {
  if (value == null || isNaN(value)) return '—';
  if (compact) {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000_000_000) return `${sign}$${(abs / 1_000_000_000_000).toFixed(2)}T`;
    if (abs >= 1_000_000_000)     return `${sign}$${(abs / 1_000_000_000).toFixed(2)}B`;
    if (abs >= 1_000_000)         return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000)             return `${sign}$${(abs / 1_000).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(2)}`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Compact elapsed-time label for "how long has this been held?" columns.
 *  Scales from seconds → minutes → hours → days → months → years so
 *  auto-trade positions (lived 12 min) render as "12m" while multi-year
 *  holdings render as "3y". Returns "—" for null / invalid dates. */
export function formatHoldDuration(purchaseDate: string | null | undefined, now: Date = new Date()): string {
  if (!purchaseDate) return '—';
  const then = new Date(purchaseDate).getTime();
  if (!isFinite(then)) return '—';
  const diffMs = now.getTime() - then;
  if (diffMs < 0) return '0s';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(days / 365);
  return `${years}y`;
}

export function formatPct(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function formatVolume(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000)     return `${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${(abs / 1_000).toFixed(1)}K`;
  return abs.toLocaleString();
}

export function formatDate(dateStr: string | null | undefined, tz?: string): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);
}

export function formatDateTime(dateStr: string | null | undefined, tz?: string): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);
}

export function formatMarketCap(value: number | null | undefined): string {
  return formatCurrency(value, true);
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
}

export function timeAgo(dateStr: string | null | undefined, tz?: string): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.max(0, Math.floor(diff / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr, tz);
}

export function getPctClass(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return 'text-text-secondary';
  if (value > 0) return 'text-gain';
  if (value < 0) return 'text-loss';
  return 'text-text-secondary';
}

export function getPctBadgeClass(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return 'bg-background-tertiary text-text-secondary px-2 py-0.5 rounded text-sm font-medium';
  if (value > 0) return 'badge-gain';
  if (value < 0) return 'badge-loss';
  return 'bg-background-tertiary text-text-secondary px-2 py-0.5 rounded text-sm font-medium';
}

/** Decode the standard XML/HTML named entities + numeric character refs.
 *  Defensive utility for fields that arrived already entity-encoded
 *  from upstream sources (e.g. SEC EDGAR Form 4 owner names like
 *  "Blue &amp; Silver Ventures, Ltd." that pre-date the backend
 *  decode in secEdgarInsiderProvider). React renders text as-is, so
 *  a stored "&amp;" displays as the literal four characters without
 *  this. Idempotent — safe to call on already-decoded strings. */
export function decodeHtmlEntities(s: string | null | undefined): string {
  if (!s) return '';
  return s
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(parseInt(d, 10)))
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    // &amp; last so we don't double-decode "&amp;lt;" → "&lt;" → "<".
    .replace(/&amp;/g, '&');
}
